import { Layers, type LucideIcon, Store, Truck, UserPlus, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { formatNumber } from '@/shared/lib/format';

import type { DashboardQuickStats } from '../types/dashboard.types';

interface QuickStat {
  key: keyof DashboardQuickStats;
  label: string;
  icon: LucideIcon;
  to: string;
}

const STATS: QuickStat[] = [
  { key: 'customers', label: 'Customers', icon: Users, to: '/customers' },
  { key: 'newCustomers', label: 'New customers', icon: UserPlus, to: '/customers' },
  { key: 'brandsOnSale', label: 'Brands on sale', icon: Store, to: '/brands' },
  { key: 'denominationsOnSale', label: 'Denominations on sale', icon: Layers, to: '/brands' },
  { key: 'activeProviders', label: 'Active providers', icon: Truck, to: '/providers' },
];

/**
 * Standing totals beside the period's order figures. The backend returns null
 * for an area the staff member may not see, and that tile is left out.
 */
export function QuickStats({ stats }: { stats: DashboardQuickStats | undefined }) {
  // `!= null`: a stat an older backend does not return at all is left out too.
  const visible = stats ? STATS.filter((stat) => stats[stat.key] != null) : [];
  if (!stats || visible.length === 0) return null;

  return (
    <section
      aria-label="Quick stats"
      className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5"
    >
      {visible.map(({ key, label, icon: Icon, to }) => (
        <Link key={key} to={to} className="focus-visible:ring-ring rounded-xl focus-visible:ring-2">
          <Card className="hover:bg-muted/40 h-full gap-0 py-3 transition-colors">
            <CardContent className="flex items-center gap-3 px-3">
              <div className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-md">
                <Icon className="size-4" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground truncate text-[11px] font-medium">{label}</p>
                <p className="font-display text-lg font-bold tabular-nums">
                  {formatNumber(stats[key])}
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </section>
  );
}
